
export class Option<T> {
    private data: T | null | undefined;
    private constructor(data: T | null | undefined) {
        this.data = data;
    }
    static Some<T>(data: T): Option<T> {
        return new Option(data);
    }
    static None<T>(): Option<T> {
        return new Option(undefined as T | null | undefined);
    }
    isSome(): boolean {
        return undefined !== this.data && null !== this.data;
    }
    isNone(): boolean {
        return undefined === this.data || null === this.data;
    }
    unwrapOr(alternate: NonNullable<T>): NonNullable<T> {
        if (this.isSome()) {
            return this.data!;
        } else {
            return alternate;
        }
    }
    unwrapOrElse(fn: () => NonNullable<T>): NonNullable<T> {
        if (this.isSome()) {
            return this.data!;
        } else {
            return fn();
        }
    }
    ignoreNone(someFn: (data: NonNullable<T>) => void) {
        if (this.isSome()) {
            someFn(this.data!);
        }
    }
    match(someFn: (data: NonNullable<T>) => void, noneFn?: () => void) {
        if (this.isSome()) {
            someFn(this.data!);
        } else {
            noneFn && noneFn();
        }
    }
    map<O>(mapFn: (data: NonNullable<T>) => O): Option<O> {
        if (this.isSome()) {
            return Option.Some(mapFn(this.data!));
        } else {
            return Option.None();
        }
    }
    replace(data: T): Option<T> {
        let oldData = this.data;
        this.data = data;
        if (undefined === oldData || null === oldData) {
            return Option.None();
        } else {
            return Option.Some(oldData);
        }
    }
    take(): Option<T> {
        let data = this.data;
        this.data = undefined;
        if (undefined === data || null === data) {
            return Option.None();
        } else {
            return Option.Some(data);
        }
    }
}

export class Result<T, E> {
    private ok: boolean;
    private data: T | null | undefined;
    private err: E | null | undefined;
    private constructor(data: T | null | undefined, err: E | null | undefined, ok: boolean) {
        this.data = data;
        this.err = err;
        this.ok = ok;
    }
    static Ok<T, E>(data: T): Result<T, E> {
        return new Result(data, undefined as E | null | undefined, true);
    }
    static Err<T, E>(err: E): Result<T, E> {
        return new Result(undefined as T | null | undefined, err, false);
    }
    isOk(): boolean {
        return this.ok;
    }
    isErr(): boolean {
        return !this.ok;
    }
    unwrapOr(alternate: NonNullable<T>): NonNullable<T> {
        if (this.isOk()) {
            return this.data!;
        } else {
            return alternate;
        }
    }
    unwrapOrElse(fn: () => NonNullable<T>): NonNullable<T> {
        if (this.isOk()) {
            return this.data!;
        } else {
            return fn();
        }
    }
    ignoreError(okFn: (data: NonNullable<T>) => void) {
        if (this.isOk()) {
            okFn(this.data!);
        }
    }
    match(okFn?: (data: NonNullable<T>) => void, errFn?: (err: NonNullable<E>) => void) {
        if (this.isOk()) {
            okFn && okFn(this.data!);
        } else {
            errFn && errFn(this.err!);
        }
    }
    map<O>(mapFn: (data: NonNullable<T>) => O): Result<O, E> {
        if (this.isOk()) {
            return Result.Ok(mapFn(this.data!));
        } else {
            return Result.Err(this.err!);
        }
    }
    mapErr<O>(mapFn: (err: E) => O): Result<T, O> {
        if (this.isOk()) {
            return Result.Ok(this.data!);
        } else {
            return Result.Err(mapFn(this.err!));
        }
    }
}

enum Enum2Type {
    First,
    Second
}

export class Enum2<T1, T2> {
    private dataType: Enum2Type;
    private data: T1 | T2;
    private constructor(dataType: Enum2Type, data: T1 | T2) {
        this.dataType = dataType;
        this.data = data;
    }
    static First<T1, T2>(data: T1): Enum2<T1, T2> {
        return new Enum2<T1, T2>(Enum2Type.First, data);
    }
    static Second<T1, T2>(data: T2): Enum2<T1, T2> {
        return new Enum2<T1, T2>(Enum2Type.Second, data);
    }
    isFirst(): boolean {
        return Enum2Type.First === this.dataType;
    }
    isSecond(): boolean {
        return Enum2Type.Second === this.dataType;
    }
    unwrapFirstOr(alternate: NonNullable<T1>): NonNullable<T1> {
        if (this.isFirst()) {
            return this.data as any;
        } else {
            return alternate;
        }
    }
    unwrapFirstOrElse(fn: () => NonNullable<T1>): NonNullable<T1> {
        if (this.isFirst()) {
            return this.data as any;
        } else {
            return fn();
        }
    }
    unwrapSecondOr(alternate: NonNullable<T2>): NonNullable<T2> {
        if (this.isSecond()) {
            return this.data as any;
        } else {
            return alternate;
        }
    }
    unwrapSecondOrElse(fn: () => NonNullable<T2>): NonNullable<T2> {
        if (this.isSecond()) {
            return this.data as any;
        } else {
            return fn();
        }
    }
    match(firstFn?: (data: NonNullable<T1>) => void, secondFn?: (data: NonNullable<T2>) => void) {
        if (this.isFirst() && firstFn) {
            firstFn(this.data as any);
        } else if (this.isSecond() && secondFn) {
            secondFn(this.data as any);
        }
    }
    matchFirst(fn: (data: NonNullable<T1>) => void) {
        if (this.isFirst()) {
            fn(this.data as any);
        }
    }
    matchSecond(fn: (data: NonNullable<T1>) => void) {
        if (this.isSecond()) {
            fn(this.data as any);
        }
    }
    mapFirst<O>(mapFn: (data: NonNullable<T1>) => O): Enum2<O, T2> {
        if (this.isFirst()) {
            return Enum2.First(mapFn(this.data as any));
        } else {
            return Enum2.Second(this.data as any);
        }
    }
    mapSecond<O>(mapFn: (data: NonNullable<T2>) => O): Enum2<T1, O> {
        if (this.isSecond()) {
            return Enum2.Second(mapFn(this.data as any));
        } else {
            return Enum2.First(this.data as any);
        }
    }
}

export interface DataObject {
    [key: string]: any;
}

export interface HtmlOption<T> {
    value: T;
    label: string;
}

export interface Response<T> {
    code: number;
    data: T;
    message: string;
}
